import { Box } from '@material-ui/core'
import { sortBy } from 'lodash'
import { useState } from 'react'
import { useNotify } from 'react-admin'
import { Controller, useForm } from 'react-hook-form'
import { TextLeft, Wrap } from 'shared/components/Base/Base'
import { Button } from 'shared/components/Button/Button'
import { Checkbox } from 'shared/components/Checkbox/Checkbox'
import { SimpleDialog } from 'shared/components/Dialog/SimpleDialog'
import { Dropdown } from 'shared/components/Dropdown/Dropdown'
import { RadioButtonGroup } from 'shared/components/RadioButtonGroup/RadioButtonGroup'
import { InlineSpinner } from 'shared/components/Spinner'
import { Text } from 'shared/components/Text/Text'
import { CurrencyInput } from 'shared/components/TextInput/CurrencyInput'
import { Handler } from 'shared/helpers/typeHelper'
import { BillingPeriod, getBillingPeriodFullName } from 'shared/models/productSubscription'
import { sizes, variants } from 'shared/theme'
import { bgGray } from 'shared/theme/colors'

import { useStarterPricing } from '../../helpers/useFlexStarterPricing'
import { changeStarterPricing, createStarterPricing } from '../../models/subscription'

interface TeamSubscriptionChangePricingProps {
  team: any
  isLoading: boolean
  onSuccess: (priceId: string) => Promise<void>
  onClose: Handler
  currentPrice?: string
}

export function TeamSubscriptionChangePricing({
  team,
  currentPrice,
  isLoading,
  onSuccess,
  onClose,
}: TeamSubscriptionChangePricingProps) {
  const [isSubmitLoading, setIsSubmitLoading] = useState(false)
  const [isCreatingPrice, setIsCreatingPrice] = useState(false)
  const { pricing, refetch: refetchPricing } = useStarterPricing(team.id)
  const [newPricing, setNewPricing] = useState<string>(currentPrice || '')
  const notify = useNotify()

  async function handleSubmit() {
    try {
      setIsSubmitLoading(true)
      await changeStarterPricing({ teamId: team?.id!, priceId: newPricing })
    } catch (err: any) {
      if (err?.response?.data) notify(JSON.stringify(err?.response?.data), { type: 'error' })
      else notify('Something went wrong', { type: 'error' })
    }

    setIsSubmitLoading(false)
    await onSuccess(newPricing)
    onClose()
  }

  async function handleCreatePriceSuccess() {
    await refetchPricing()
    setIsCreatingPrice(false)
  }

  if (!pricing)
    return (
      <SimpleDialog isOpen onClose={onClose}>
        <Wrap width="400px" maxWidth="100%">
          <InlineSpinner mt={8} mb={8} />
        </Wrap>
      </SimpleDialog>
    )

  if (isCreatingPrice) {
    return (
      <SimpleDialog isOpen onClose={onClose}>
        <Wrap width="400px" maxWidth="100%">
          <CreatePriceForm
            team={team}
            onSuccess={handleCreatePriceSuccess}
            onCancel={() => setIsCreatingPrice(false)}
          />
        </Wrap>
      </SimpleDialog>
    )
  }

  const options = sortBy(pricing, (p) => p.billing_period).map((price: any) => ({
    value: price.id,
    label: getPriceName(price.description, price.billing_period),
  }))

  return (
    <SimpleDialog isOpen onClose={onClose}>
      <Wrap width="400px" maxWidth="100%">
        <Text variant="header2" mb={3}>
          Change price for the team
        </Text>
        <TextLeft>
          <RadioButtonGroup
            label="Select price"
            options={options}
            onChange={(e) => setNewPricing(e.target.value)}
            value={newPricing}
            noMargin
          />

          <Box m={5} />
          <Button
            onClick={handleSubmit}
            size={sizes.large}
            margin="0 auto"
            isLoading={isLoading || isSubmitLoading}
          >
            Confirm
          </Button>
          <Box m={3} />
          <Button margin="0 auto" variant={variants.text} onClick={() => setIsCreatingPrice(true)}>
            Create custom price
          </Button>
          <Box m={3} />
          <Button margin="0 auto" variant={variants.text} onClick={onClose}>
            Cancel
          </Button>
        </TextLeft>
      </Wrap>
    </SimpleDialog>
  )
}

interface CreatePriceFormProps {
  team: any
  onSuccess: Handler
  onCancel: Handler
}

interface CreatePriceFormValues {
  amount: number | null
  is_flat: boolean
  billing_period: BillingPeriod
}

function CreatePriceForm({ team, onSuccess, onCancel }: CreatePriceFormProps) {
  const [isLoading, setIsLoading] = useState(false)
  const notify = useNotify()

  const {
    handleSubmit: handleFormSubmit,
    control,
    watch,
  } = useForm<CreatePriceFormValues>({
    defaultValues: {
      amount: null,
      is_flat: true,
      billing_period: BillingPeriod.QUARTERLY,
    },
    mode: 'onSubmit',
  })
  const billingPeriod = watch('billing_period')
  const isFlat = watch('is_flat')

  async function onSubmit(formFields: CreatePriceFormValues) {
    try {
      setIsLoading(true)
      await createStarterPricing({
        teamId: team.id,
        amount: String(formFields.amount).replaceAll(',', ''),
        is_flat: formFields.is_flat,
        billing_period: formFields.billing_period,
      })
      await onSuccess()
    } catch (err: any) {
      if (err?.response?.data) notify(JSON.stringify(err?.response?.data), { type: 'error' })
      else notify('Something went wrong', { type: 'error' })
    }
    setIsLoading(false)
  }

  return (
    <form onSubmit={handleFormSubmit(onSubmit)}>
      <Text variant="header2" mb={3}>
        Create new price
      </Text>
      <Controller
        render={({ field, fieldState }) => (
          <CurrencyInput
            {...field}
            value={field.value || ''}
            error={fieldState.error ? 'Amount is required' : undefined}
            label="Amount"
            helperText={`Amount that will be charged each ${getBillingPeriodName(billingPeriod)} ${
              isFlat ? '' : 'per user'
            }`}
            backgroundColor={bgGray}
            inputMode="numeric"
            noMargin
          />
        )}
        name="amount"
        control={control}
        rules={{
          validate: {
            validate: (value) => value !== null && Number(String(value).replaceAll(',', '')) > 0,
          },
        }}
      />

      <Box m={5} />
      <Controller
        render={({ field, fieldState }) => (
          <Dropdown
            {...field}
            error={!!fieldState.error}
            options={[
              {
                value: BillingPeriod.MONTHLY,
                label: getBillingPeriodFullName(BillingPeriod.MONTHLY),
              },
              {
                value: BillingPeriod.YEARLY,
                label: getBillingPeriodFullName(BillingPeriod.YEARLY),
              },
              {
                value: BillingPeriod.QUARTERLY,
                label: getBillingPeriodFullName(BillingPeriod.QUARTERLY),
              },
            ]}
            label="Select type"
            maxHeight={300}
            isFullWidth
            noMargin
            backgroundColor={bgGray}
          />
        )}
        name="billing_period"
        control={control}
        rules={{ required: true }}
      />

      <Box m={1} />
      <Controller
        render={({ field, fieldState }) => (
          <Checkbox {...field} label="Is flat price" error={fieldState.error?.message} />
        )}
        name="is_flat"
        control={control}
      />

      <Box m={3} />
      <Button type="submit" size={sizes.large} margin="0 auto" isLoading={isLoading}>
        Confirm
      </Button>
      <Box m={3} />
      <Button margin="0 auto" variant={variants.text} onClick={onCancel}>
        Cancel
      </Button>
    </form>
  )
}

// Changing actual description of price is bit more complex as descriptions
// and used in some cases as identifier, so we have this helper to render
// different description in client
function getPriceName(price: string, billing_period: BillingPeriod) {
  if (price === 'Flex starter seat monthly price') return '$30/month per seat'
  if (price === '150$ Flat after 4 members') return '$150/month flat rate for 5+ seats'
  return (
    <>
      {price} <br /> (per {getBillingPeriodName(billing_period)})
    </>
  )
}

function getBillingPeriodName(billingPeriod: BillingPeriod) {
  if (billingPeriod === BillingPeriod.MONTHLY) return 'month'
  if (billingPeriod === BillingPeriod.YEARLY) return 'year'
  if (billingPeriod === BillingPeriod.QUARTERLY) return 'quarter'
  return ''
}
